/**
 * EraserOptionsBar — réglages contextuels de la gomme.
 *
 * Bandeau horizontal glissé entre la barre de menus (Fichier / Ajout /
 * Historique) et les onglets de documents. Il n'apparaît QUE lorsque la gomme
 * est active : c'est aujourd'hui le seul outil à porter des réglages, et une
 * barre vide en permanence coûterait de la hauteur de canvas pour rien.
 *
 * Le rayon est exprimé en millimètres, comme tout le reste du document : le
 * trait garde ainsi la même épaisseur réelle quel que soit le niveau de zoom.
 */
import React from 'react';
import { useLabelStore } from '../store/useLabelStore';
import { flattenForRender } from '../utils/layerTree';

const RADII = [1, 2, 5, 10, 20, 40];

export default function EraserOptionsBar() {
    const activeTool = useLabelStore((s) => s.activeTool);
    const eraser = useLabelStore((s) => s.eraser);
    const setEraser = useLabelStore((s) => s.setEraser);
    const elements = useLabelStore((s) => s.elements);
    const selectedElementIds = useLabelStore((s) => s.selectedElementIds);

    if (activeTool !== 'eraser') return null;

    const target = elements.find(
        (e) => e.type === 'image' && selectedElementIds.includes(e.id)
    );

    // Verrou EFFECTIF, cascade des groupes comprise — la même notion que celle
    // dont LabelCanvas se sert pour refuser de monter la surface de peinture.
    // « Tout restaurer » ne le consultait pas : le bouton restait actif sur un
    // calque verrouillé et effaçait son masque, alors que la gomme, elle, ne
    // pouvait plus y peindre un seul pixel.
    const targetLocked = !!target && !!flattenForRender(elements)
        .find((x) => x.el.id === target.id)?.effLocked;

    const resetMask = () => {
        if (!target || targetLocked) return;
        useLabelStore.getState().updateElement(target.id, { eraseMask: null });
        useLabelStore.getState()._pushState('Réinitialisation de la gomme');
    };

    // Pas de z-index sur la barre : elle vit dans le flux, entre la barre de
    // menus et les onglets, et n'ouvre aucun menu déroulant à faire passer
    // au-dessus des règles. (Le palier 85 est déjà celui du coin des règles.)
    return (
        <div className="shrink-0 bg-black/25 border-b border-white/10 px-4 py-1.5 flex items-center gap-4 text-[11px] font-mono">
            {/* Identité de l'outil */}
            <div className="flex items-center gap-2 shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-forge-accent">
                    <path d="m7 21-4.3-4.3a1 1 0 0 1 0-1.4l9.6-9.6a2 2 0 0 1 2.8 0l4.9 4.9a2 2 0 0 1 0 2.8L13 21" />
                    <path d="M22 21H7" /><path d="m5 11 9 9" />
                </svg>
                <span className="text-[9px] uppercase tracking-widest font-bold text-forge-accent">Gomme</span>
            </div>

            <div className="w-px h-4 bg-white/10 shrink-0" />

            {/* Bord : net ou flou */}
            <div className="flex items-center gap-2 shrink-0">
                <span className="text-[9px] uppercase tracking-widest text-[rgba(var(--forge-muted),0.55)]">Bord</span>
                <div className="flex bg-black/40 p-0.5 rounded-md border border-white/5">
                    {[{ id: 'hard', label: 'Net' }, { id: 'soft', label: 'Flou' }].map((o) => (
                        <button
                            key={o.id}
                            type="button"
                            onClick={() => setEraser({ hardness: o.id })}
                            aria-pressed={eraser.hardness === o.id}
                            className={`px-2.5 py-1 text-[9px] uppercase tracking-wider rounded transition-colors ${
                                eraser.hardness === o.id
                                    ? 'bg-white/15 text-[rgba(var(--forge-text),1)] font-bold'
                                    : 'text-[rgba(var(--forge-muted),0.5)] hover:text-[rgba(var(--forge-text),1)] hover:bg-white/5'
                            }`}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Rayon */}
            <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                <span className="text-[9px] uppercase tracking-widest text-[rgba(var(--forge-muted),0.55)] shrink-0">Rayon</span>
                <input
                    type="range"
                    min="0.5" max="50" step="0.5"
                    value={eraser.radius}
                    onChange={(e) => setEraser({ radius: parseFloat(e.target.value) })}
                    aria-label="Rayon de la gomme en millimètres"
                    className="w-28 min-w-[70px] shrink accent-[rgb(var(--forge-accent))] cursor-pointer"
                />
                <input
                    type="number"
                    min="0.5" max="50" step="0.5"
                    value={eraser.radius}
                    onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (!Number.isNaN(v)) setEraser({ radius: Math.min(50, Math.max(0.5, v)) });
                    }}
                    aria-label="Rayon de la gomme"
                    className="w-12 shrink-0 bg-black/40 border border-white/10 rounded px-1.5 py-0.5 text-[10px] text-[rgba(var(--forge-text),1)] focus:border-forge-accent/50 focus:outline-none"
                />
                <span className="text-[9px] text-[rgba(var(--forge-muted),0.45)] shrink-0">mm</span>

                {/* Tailles courantes — évite de viser au curseur pour les valeurs
                    usuelles. Réservées aux très larges écrans : la zone centrale
                    de l'éditeur est étroite, les panneaux latéraux mangeant plus
                    de la moitié de la fenêtre. */}
                <div className="hidden 2xl:flex items-center gap-1 ml-1 shrink-0">
                    {RADII.map((r) => (
                        <button
                            key={r}
                            type="button"
                            onClick={() => setEraser({ radius: r })}
                            className={`px-1.5 py-0.5 text-[9px] rounded transition-colors ${
                                eraser.radius === r
                                    ? 'bg-forge-accent/20 text-forge-accent border border-forge-accent/30'
                                    : 'text-[rgba(var(--forge-muted),0.45)] hover:text-[rgba(var(--forge-text),1)] hover:bg-white/5 border border-transparent'
                            }`}
                        >
                            {r}
                        </button>
                    ))}
                </div>
            </div>

            {/* État de la cible + réinitialisation */}
            <div className="flex items-center gap-3 shrink-0">
                {target ? (
                    <>
                        {targetLocked && (
                            <span className="text-[9px] text-amber-400/80 uppercase tracking-wider">
                                Calque verrouillé
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={resetMask}
                            disabled={!target.eraseMask || targetLocked}
                            title={targetLocked
                                ? 'Calque verrouillé : déverrouillez-le pour restaurer les pixels effacés'
                                : 'Restaurer tous les pixels effacés de ce calque'}
                            className={`px-2.5 py-1 text-[9px] uppercase tracking-wider rounded border transition-colors ${
                                target.eraseMask && !targetLocked
                                    ? 'text-[rgba(var(--forge-muted),0.8)] border-white/10 hover:text-[rgba(var(--forge-text),1)] hover:bg-white/10'
                                    : 'text-[rgba(var(--forge-muted),0.25)] border-white/5 cursor-not-allowed'
                            }`}
                        >
                            Tout restaurer
                        </button>
                    </>
                ) : (
                    <span className="text-[9px] text-amber-400/80 uppercase tracking-wider">
                        Sélectionnez un calque image
                    </span>
                )}
            </div>
        </div>
    );
}
